import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { PALETTE, matte, colored, vary, seededRandom, jitter } from '../../Style.js';

// Neutral/claimable zone color: dormant, chalky runes
const NEUTRAL_COLOR = '#b8b09a';
const DIMMED_COLOR = '#555555';
const GOLD = 0xd9b25a;

// Rune strokes in a unit cell: u runs along the ring, v away from the centre
// (or upwards on a standing stone). Each stroke is [u1, v1, u2, v2].
const GLYPHS = [
  [[0, -0.5, 0, 0.5], [0, 0.1, 0.35, 0.4], [0, -0.2, 0.35, 0.1]],
  [[0, -0.5, 0, 0.5], [0, 0.5, -0.3, 0.2], [0, 0.5, 0.3, 0.2]],
  [[0, -0.5, 0.35, 0], [0.35, 0, 0, 0.5], [0, 0.5, -0.35, 0], [-0.35, 0, 0, -0.5]],
  [[-0.3, -0.5, 0.3, 0.5], [0.3, -0.5, -0.3, 0.5]],
  [[0, -0.5, 0, 0.5], [-0.25, -0.1, 0.25, 0.15]],
  [[-0.2, -0.5, -0.2, 0.5], [0.2, -0.5, 0.2, 0.5], [-0.2, 0.3, 0.2, -0.1]]
];

// A recruiting ground: a ring of carved paving stones with glowing runes in
// the owner's colour, a few standing stones, and a painted figurine of the
// unit on sale turning on a central dais.
export class BuyZone {
  constructor(scene, zone) {
    this.scene = scene;
    this.zone = zone;
    this.mesh = null; // Root group, positioned at the zone centre
    this.label = null;
    this.glowMesh = null;
    this.inlayMesh = null;
    this.iconMesh = null;
    this.iconMaterials = [];
    this.canAfford = true;
    this.baseColor = this.getZoneColor(zone.ownerId);
    this.rand = seededRandom(hashId(zone.id));
    this.phase = this.rand() * Math.PI * 2;
    this.create();
  }

  getZoneColor(ownerId) {
    if (ownerId === -1) {
      return NEUTRAL_COLOR;
    }
    return PLAYER_COLORS[ownerId];
  }

  create() {
    const rand = this.rand;
    const radius = this.zone.radius;
    const hasIcon = !!this.zone.unitType;

    this.mesh = new THREE.Group();
    // A hair of per-zone lift so overlapping circles don't z-fight
    const baseY = (this.zone.position.y || 0) + rand() * 0.03;
    this.mesh.position.set(this.zone.position.x, baseY, this.zone.position.z);

    const stone = [];
    const runes = [];

    // Packed earth floor and a central dais for zones that sell a unit
    if (hasIcon) {
      stone.push(colored(new THREE.CylinderGeometry(radius - 0.75, radius - 0.75, 0.1, 20), vary(PALETTE.dirt, rand, 0.05), { y: 0.05 }));
      stone.push(colored(jitter(new THREE.CylinderGeometry(1.15, 1.3, 0.34, 8), 0.06, rand), vary(PALETTE.stoneLight, rand, 0.08), { y: 0.17 }));
      runes.push(colored(new THREE.RingGeometry(radius - 0.86, radius - 0.78, 40), 0xffffff, { rx: -Math.PI / 2, y: 0.11 }));
    } else {
      runes.push(colored(new THREE.RingGeometry(radius - 0.9, radius - 0.82, 64), 0xffffff, { rx: -Math.PI / 2, y: 0.03 }));
    }

    // Ring of carved paving slabs, every few carrying a rune
    const ringRadius = radius - 0.4;
    const slabs = Math.max(10, Math.round((Math.PI * 2 * ringRadius) / 1.5));
    const slabWidth = (Math.PI * 2 * ringRadius) / slabs - 0.08;
    const runeEvery = hasIcon ? 2 : 3;
    for (let i = 0; i < slabs; i++) {
      const a = (i / slabs) * Math.PI * 2;
      const color = rand() < 0.15 ? vary(PALETTE.mossStone, rand) : vary(PALETTE.stone, rand, 0.12, 0.02);
      const slab = jitter(new THREE.BoxGeometry(slabWidth, 0.22, 0.8), 0.04, rand);
      stone.push(colored(slab, color, { y: 0.11, z: ringRadius }).rotateY(a));

      if (i % runeEvery === 0) {
        const glyph = GLYPHS[Math.floor(rand() * GLYPHS.length)];
        for (const geo of glyphStrokes(glyph, 0.55, (u, v) => new THREE.Vector3(u, 0.25, ringRadius + v), true)) {
          runes.push(geo.rotateY(a));
        }
      }
    }

    // Standing stones on the ring, runes facing inward
    const stones = hasIcon ? 3 : 8;
    for (let i = 0; i < stones; i++) {
      const a = ((i + 0.5 + (rand() - 0.5) * 0.4) / stones) * Math.PI * 2;
      const height = (hasIcon ? 1.1 : 1.8) + rand() * 0.6;
      const r = ringRadius + 0.05;
      const geo = jitter(new THREE.BoxGeometry(0.7, height, 0.45, 1, 2, 1), 0.12, rand);
      // Taper towards the top
      const pos = geo.attributes.position;
      for (let j = 0; j < pos.count; j++) {
        const t = 1 - 0.3 * (pos.getY(j) / height + 0.5);
        pos.setX(j, pos.getX(j) * t);
        pos.setZ(j, pos.getZ(j) * t);
      }
      geo.computeVertexNormals();
      const color = rand() < 0.4 ? vary(PALETTE.mossStone, rand) : vary(PALETTE.stoneDark, rand, 0.1);
      stone.push(colored(geo, color, { y: height / 2 + 0.1, z: r, rz: (rand() - 0.5) * 0.1 }).rotateY(a));

      const glyph = GLYPHS[Math.floor(rand() * GLYPHS.length)];
      const faceZ = r - 0.28;
      for (const g of glyphStrokes(glyph, 0.5, (u, v) => new THREE.Vector3(u, height * 0.55 + v, faceZ), false)) {
        runes.push(g.rotateY(a));
      }
    }

    const stoneMesh = new THREE.Mesh(mergeGeometries(stone), matte(0xffffff, { vertexColors: true }));
    stoneMesh.castShadow = true;
    stoneMesh.receiveShadow = true;
    this.mesh.add(stoneMesh);

    // Glowing runes in the owner's colour
    const glowMaterial = new THREE.MeshBasicMaterial({
      color: this.baseColor,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.6
    });
    this.glowMesh = new THREE.Mesh(mergeGeometries(runes), glowMaterial);
    this.mesh.add(this.glowMesh);

    if (hasIcon) {
      // Owner-coloured inlay set into the dais
      const inlayMaterial = new THREE.MeshStandardMaterial({
        color: this.baseColor,
        emissive: this.baseColor,
        emissiveIntensity: 0.35,
        roughness: 0.7,
        flatShading: true
      });
      this.inlayMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.85, 0.04, 8), inlayMaterial);
      this.inlayMesh.position.y = 0.35;
      this.mesh.add(this.inlayMesh);
    }

    // Add a figurine on the dais based on unit type
    this.createUnitIcon(0.37);

    this.scene.add(this.mesh);
  }

  createUnitIcon(baseY) {
    const unitType = this.zone.unitType;
    if (!unitType) return; // No icon for base zones

    // Super units are larger, with gold accents
    const isSuper = unitType.startsWith('super_');
    const scale = isSuper ? 1.3 : 1.0;

    let parts = null;
    let glow = null;

    if (unitType === 'tank' || unitType === 'super_tank') {
      parts = mountedKnight(isSuper);
    } else if (unitType === 'airplane' || unitType === 'super_helicopter') {
      parts = isSuper ? winged(0x4f5a5e, 1.35, true) : winged(0x6d8a3e, 1.0, false);
    } else if (unitType === 'sniper') {
      parts = ranger();
    } else if (unitType === 'rocket_launcher') {
      parts = trebuchet();
    }

    if (!parts) return;

    const icon = new THREE.Group();
    const material = matte(0xffffff, { vertexColors: true, unique: true });
    material.transparent = true;
    this.iconMaterials.push(material);
    const figure = new THREE.Mesh(mergeGeometries(parts), material);
    figure.castShadow = true;
    icon.add(figure);
    if (glow) {
      this.iconMaterials.push(glow.material);
      icon.add(glow);
    }

    icon.scale.setScalar(scale);
    icon.position.y = baseY;
    icon.userData.baseY = baseY;
    this.mesh.add(icon);
    this.iconMesh = icon;
  }

  // Update for animation (pulsing runes, turning figurine)
  update(time) {
    if (this.glowMesh) {
      if (this.canAfford) {
        const pulse = 0.4 + Math.sin(time * 3) * 0.2;
        this.glowMesh.material.opacity = pulse;
      } else {
        // Dimmed when can't afford
        this.glowMesh.material.opacity = 0.1;
      }
    }

    if (this.iconMesh) {
      this.iconMesh.rotation.y = time * 0.5 + this.phase;
      this.iconMesh.position.y = this.iconMesh.userData.baseY + (Math.sin(time * 1.6 + this.phase) * 0.5 + 0.5) * 0.12;
    }
  }

  // Set whether the player can afford this zone's unit
  setAffordable(canAfford) {
    if (this.canAfford === canAfford) return;

    this.canAfford = canAfford;

    if (this.inlayMesh) {
      const color = canAfford ? this.baseColor : DIMMED_COLOR;
      this.inlayMesh.material.color.set(color);
      this.inlayMesh.material.emissive.set(color);
      this.inlayMesh.material.emissiveIntensity = canAfford ? 0.35 : 0;
    }
    for (const material of this.iconMaterials) {
      material.opacity = canAfford ? 1.0 : 0.3;
    }
  }

  // Check if a position is within this buy zone
  isInRange(position) {
    const dx = position.x - this.zone.position.x;
    const dz = position.z - this.zone.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.zone.radius * this.zone.radius;
  }

  // Update the owner of this zone (when claimed)
  updateOwner(newOwnerId) {
    if (this.zone.ownerId === newOwnerId) return;

    this.zone.ownerId = newOwnerId;
    this.baseColor = this.getZoneColor(newOwnerId);

    // Re-colour the runes and inlay
    if (this.glowMesh) {
      this.glowMesh.material.color.set(this.baseColor);
    }
    if (this.inlayMesh && this.canAfford) {
      this.inlayMesh.material.color.set(this.baseColor);
      this.inlayMesh.material.emissive.set(this.baseColor);
    }
  }

  remove() {
    if (!this.mesh) return;
    this.scene.remove(this.mesh);
    this.mesh.traverse(child => {
      if (child.isMesh) child.geometry.dispose();
    });
    this.glowMesh.material.dispose();
    if (this.inlayMesh) this.inlayMesh.material.dispose();
    this.iconMaterials.forEach(material => material.dispose());
  }
}

// Thin bars tracing a glyph. `place` maps cell (u, v) to a local position;
// flat glyphs lie on a surface, upright ones are cut into a vertical face.
function glyphStrokes(glyph, size, place, flat) {
  return glyph.map(([u1, v1, u2, v2]) => {
    const a = place(u1 * size, v1 * size);
    const b = place(u2 * size, v2 * size);
    const dir = new THREE.Vector3().subVectors(b, a);
    const length = dir.length() + 0.06;
    const geo = colored(flat ? new THREE.BoxGeometry(0.07, 0.01, length) : new THREE.BoxGeometry(0.07, length, 0.01), 0xffffff);
    const axis = flat ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
    geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(axis, dir.normalize()));
    const mid = a.add(b).multiplyScalar(0.5);
    return geo.translate(mid.x, mid.y, mid.z);
  });
}

// --- Figurines, built facing +Z on the dais top ---

// Mounted knight: a horse in a caparison with a couched lance and kite shield
function mountedKnight(isPaladin) {
  const parts = [];
  const coat = isPaladin ? 0xd6d0c4 : 0x7a4a2a;
  const mane = isPaladin ? 0x9a948a : 0x2e2018;
  const drape = isPaladin ? PALETTE.cloth : 0xb8873a;
  const trim = isPaladin ? GOLD : PALETTE.woodDark;
  const steel = 0xaab0b8;

  // Horse: legs, caparisoned barrel, neck and head
  for (const x of [-0.12, 0.12]) {
    for (const z of [-0.36, 0.34]) {
      parts.push(colored(new THREE.BoxGeometry(0.09, 0.5, 0.1), coat, { x, y: 0.25, z }));
    }
  }
  parts.push(colored(new THREE.SphereGeometry(1, 7, 5), drape, { y: 0.66, scale: [0.22, 0.21, 0.52] }));
  parts.push(colored(new THREE.BoxGeometry(0.4, 0.26, 1.0), drape, { y: 0.5 }));
  parts.push(colored(new THREE.BoxGeometry(0.42, 0.04, 1.02), trim, { y: 0.38 }));
  parts.push(colored(new THREE.BoxGeometry(0.16, 0.44, 0.22), coat, { y: 0.92, z: 0.5, rx: 0.6 }));
  parts.push(colored(new THREE.BoxGeometry(0.14, 0.14, 0.34), isPaladin ? steel : coat, { y: 1.1, z: 0.73, rx: 0.9 }));
  parts.push(colored(new THREE.BoxGeometry(0.05, 0.4, 0.06), mane, { y: 0.98, z: 0.41, rx: 0.6 }));
  parts.push(colored(new THREE.BoxGeometry(0.07, 0.38, 0.08), mane, { y: 0.62, z: -0.58, rx: 0.4 }));

  // Rider: tabard, helm with plume or crest
  parts.push(colored(new THREE.BoxGeometry(0.24, 0.3, 0.16), drape, { y: 1.03, z: -0.05 }));
  parts.push(colored(new THREE.BoxGeometry(0.26, 0.04, 0.18), trim, { y: 0.92, z: -0.05 }));
  parts.push(colored(new THREE.CylinderGeometry(0.08, 0.09, 0.18, 7), steel, { y: 1.28, z: -0.05 }));
  if (isPaladin) {
    parts.push(colored(new THREE.BoxGeometry(0.03, 0.12, 0.2), GOLD, { y: 1.42, z: -0.05 }));
    parts.push(colored(new THREE.BoxGeometry(0.03, 0.14, 0.02), GOLD, { y: 1.04, z: 0.04 }));
    parts.push(colored(new THREE.BoxGeometry(0.1, 0.03, 0.02), GOLD, { y: 1.07, z: 0.04 }));
    // Banner on the rider's back
    parts.push(colored(new THREE.CylinderGeometry(0.015, 0.015, 0.8, 5), PALETTE.woodDark, { y: 1.35, z: -0.18 }));
    parts.push(colored(new THREE.BoxGeometry(0.3, 0.3, 0.02), PALETTE.cloth, { y: 1.55, z: -0.19 }));
    parts.push(colored(new THREE.BoxGeometry(0.31, 0.03, 0.03), GOLD, { y: 1.4, z: -0.19 }));
  } else {
    parts.push(colored(new THREE.ConeGeometry(0.08, 0.1, 7), steel, { y: 1.42, z: -0.05 }));
    parts.push(colored(new THREE.SphereGeometry(1, 5, 4), 0xb8873a, { y: 1.47, z: -0.12, rx: -0.6, scale: [0.04, 0.05, 0.14] }));
  }

  // Raised lance with a vamplate and pennon, and a kite shield on the far side
  const lance = (geo, t, back = 0) => geo.translate(0, t, back).rotateX(0.5).translate(0.14, 0.85, 0.0);
  parts.push(lance(colored(new THREE.CylinderGeometry(0.015, 0.03, 1.1, 5), isPaladin ? 0xf1ece0 : PALETTE.woodLight), 0.55));
  parts.push(lance(colored(new THREE.ConeGeometry(0.07, 0.12, 6), isPaladin ? GOLD : steel), 0.3));
  parts.push(lance(colored(new THREE.BoxGeometry(0.01, 0.2, 0.12), isPaladin ? PALETTE.cloth : 0xb8873a), 0.92, -0.07));
  const shape = new THREE.Shape([[-0.12, 0.14], [0.12, 0.14], [0.12, 0.01], [0, -0.22], [-0.12, 0.01]].map(([u, v]) => new THREE.Vector2(u, v)));
  parts.push(colored(new THREE.ExtrudeGeometry(shape, { depth: 0.03, bevelEnabled: false }), isPaladin ? PALETTE.cloth : 0xb8873a, {
    ry: -Math.PI / 2, x: -0.15, y: 1.02, z: 0.0
  }));
  parts.push(colored(new THREE.BoxGeometry(0.01, 0.05, 0.22), trim, { x: -0.185, y: 1.06, z: 0.0 }));
  return parts;
}

// Wyvern / Dragon on a perch, wings spread
function winged(hide, span, isDragon) {
  const parts = [];
  const y = 1.0;
  parts.push(colored(new THREE.CylinderGeometry(0.06, 0.1, 0.65, 5), PALETTE.woodDark, { y: 0.32 }));
  parts.push(colored(new THREE.SphereGeometry(0.32, 6, 4), hide, { y, scale: [1, 0.8, 1.9] }));
  parts.push(colored(new THREE.ConeGeometry(0.15, 0.7, 5), hide, { rx: Math.PI / 2 - 0.5, y: y + 0.25, z: 0.65 }));
  parts.push(colored(new THREE.BoxGeometry(0.22, 0.18, 0.36), hide, { y: y + 0.45, z: 1.0 }));
  parts.push(colored(new THREE.ConeGeometry(0.14, 1.1, 5), hide, { rx: -Math.PI / 2 - 0.2, y: y - 0.1, z: -1.05 }));
  parts.push(colored(new THREE.ConeGeometry(0.1, 0.2, 3), isDragon ? GOLD : PALETTE.woodDark, { rx: -Math.PI / 2, y: y - 0.22, z: -1.65 }));

  const membrane = new THREE.Color(hide).offsetHSL(0, -0.1, 0.12);
  for (const side of [-1, 1]) {
    const pts = [[0, 0.3], [1.6, 0.15], [1.25, -0.3], [0.85, -0.12], [0.55, -0.5], [0, -0.4]];
    const shape = new THREE.Shape(pts.map(([px, py]) => new THREE.Vector2(px * span * side, py * span)));
    const wing = new THREE.ExtrudeGeometry(shape, { depth: 0.04, bevelEnabled: false });
    parts.push(colored(wing, membrane, { rx: Math.PI / 2, rz: side * 0.35, x: side * 0.18, y: y + 0.15 }));
    if (isDragon) {
      parts.push(colored(new THREE.ConeGeometry(0.05, 0.3, 4), GOLD, { rx: -0.6, x: side * 0.08, y: y + 0.62, z: 0.92 }));
    }
  }
  return parts;
}

// Longbow Ranger: hooded archer with a bow taller than the waist
function ranger() {
  const parts = [];
  const green = 0x4f6b3a;
  for (const x of [-0.12, 0.12]) {
    parts.push(colored(new THREE.BoxGeometry(0.16, 0.6, 0.18), PALETTE.woodDark, { x, y: 0.3 }));
  }
  parts.push(colored(new THREE.CylinderGeometry(0.24, 0.3, 0.7, 6), green, { y: 0.95 }));
  parts.push(colored(new THREE.BoxGeometry(0.62, 0.06, 0.34), PALETTE.woodDark, { y: 0.78 }));
  parts.push(colored(new THREE.SphereGeometry(0.17, 6, 4), 0xd9a77a, { y: 1.45 }));
  parts.push(colored(new THREE.ConeGeometry(0.24, 0.5, 6), 0x3f5a2e, { y: 1.62, z: -0.03 }));
  parts.push(colored(new THREE.CylinderGeometry(0.09, 0.09, 0.6, 6), PALETTE.wood, { y: 1.1, z: -0.28, rx: 0.3 }));
  for (const x of [-0.04, 0.04]) {
    parts.push(colored(new THREE.BoxGeometry(0.03, 0.12, 0.03), PALETTE.cloth, { x, y: 1.45, z: -0.38, rx: 0.3 }));
  }
  const arc = Math.PI * 0.8;
  parts.push(colored(new THREE.TorusGeometry(0.8, 0.035, 3, 10, arc), PALETTE.woodLight, { rz: -arc / 2, x: 0.15, y: 1.0, z: 0.2 }));
  const chord = 0.8 * Math.cos(arc / 2);
  parts.push(colored(new THREE.BoxGeometry(0.015, 1.6 * Math.sin(arc / 2), 0.015), PALETTE.cloth, { x: 0.15 + chord, y: 1.0, z: 0.2 }));
  return parts;
}

// Trebuchet: a wheeled base, an A-frame, and a throwing arm cocked with its
// counterweight raised and the sling trailing behind
function trebuchet() {
  const parts = [];

  // Base frame on four wheels
  for (const x of [-0.32, 0.32]) {
    parts.push(colored(new THREE.BoxGeometry(0.1, 0.1, 1.4), PALETTE.woodDark, { x, y: 0.22 }));
    for (const z of [-0.5, 0.5]) {
      parts.push(colored(new THREE.CylinderGeometry(0.18, 0.18, 0.08, 8), PALETTE.woodDark, { rz: Math.PI / 2, x: x * 1.3, y: 0.18, z }));
      parts.push(colored(new THREE.CylinderGeometry(0.05, 0.05, 0.1, 6), PALETTE.iron, { rz: Math.PI / 2, x: x * 1.3, y: 0.18, z }));
    }
  }
  for (const z of [-0.6, 0, 0.6]) {
    parts.push(colored(new THREE.BoxGeometry(0.74, 0.08, 0.1), PALETTE.wood, { y: 0.3, z }));
  }

  // A-frame uprights on each side meeting at the axle
  for (const x of [-0.3, 0.3]) {
    parts.push(colored(new THREE.BoxGeometry(0.08, 1.1, 0.08), PALETTE.wood, { x, y: 0.78, z: -0.2, rx: 0.38 }));
    parts.push(colored(new THREE.BoxGeometry(0.08, 1.1, 0.08), PALETTE.wood, { x, y: 0.78, z: 0.2, rx: -0.38 }));
  }
  parts.push(colored(new THREE.CylinderGeometry(0.045, 0.045, 0.72, 6), PALETTE.iron, { rz: Math.PI / 2, y: 1.28 }));

  // Throwing arm: long end raised behind, counterweight hanging forward
  const tilt = 0.55;
  parts.push(colored(new THREE.BoxGeometry(0.08, 0.08, 1.7), PALETTE.woodLight, { y: 1.28 + 0.25 * Math.sin(tilt), z: -0.25 * Math.cos(tilt), rx: tilt }));
  const shortEnd = { y: 1.28 - 0.5 * Math.sin(tilt), z: 0.5 * Math.cos(tilt) };
  parts.push(colored(new THREE.BoxGeometry(0.03, 0.2, 0.03), PALETTE.iron, { y: shortEnd.y - 0.1, z: shortEnd.z }));
  parts.push(colored(new THREE.BoxGeometry(0.3, 0.26, 0.26), PALETTE.stoneDark, { y: shortEnd.y - 0.3, z: shortEnd.z }));
  parts.push(colored(new THREE.BoxGeometry(0.32, 0.05, 0.28), GOLD, { y: shortEnd.y - 0.2, z: shortEnd.z }));

  // Sling with a boulder at the end of the long arm
  const longEnd = { y: 1.28 + 1.1 * Math.sin(tilt), z: -1.1 * Math.cos(tilt) };
  parts.push(colored(new THREE.BoxGeometry(0.02, 0.4, 0.02), PALETTE.cloth, { y: longEnd.y - 0.2, z: longEnd.z - 0.05, rx: 0.25 }));
  parts.push(colored(new THREE.DodecahedronGeometry(0.1, 0), PALETTE.stone, { y: longEnd.y - 0.42, z: longEnd.z - 0.1 }));
  return parts;
}

function hashId(id) {
  let h = 2166136261;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
